import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { flowsApi, type AiFlow } from '../../lib/api';
import { sx } from './styles';

// Intents the classifier emits (mirrors SLOT_SCHEMAS in ai-brain). A flow fires
// when the turn's intent is checked OR one of its keywords appears.
const INTENTS = ['technical', 'coverage', 'activation', 'porting', 'billing', 'account'];

type Draft = {
  id?: string;
  title: string;
  intents: string[];
  keywords: string; // comma-separated in the form
  steps: string[];
  status: 'draft' | 'approved';
};

const EMPTY_DRAFT: Draft = { title: '', intents: ['technical'], keywords: '', steps: [''], status: 'draft' };

function toDraft(f: AiFlow): Draft {
  return {
    id: f.id,
    title: f.title,
    intents: f.trigger?.intents ?? [],
    keywords: (f.trigger?.keywords ?? []).join(', '),
    steps: f.steps.length ? f.steps.map((s) => s.text) : [''],
    status: f.status,
  };
}

// Where a GENERATED draft came from. Hand-authored flows have no `source`, so
// every provenance field is optional and rendered defensively — an older doc,
// or a doc written by a different producer, must never blank the row.
const SOURCE_LABEL: Record<string, string> = {
  correction: 'From an agent correction',
  feedback: 'From repeated bad-answer feedback',
  manual: 'Hand-written',
};

function clip(s: string, n: number): string {
  const t = s.trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

// A flow with no intent AND no keyword is loaded by the bot but matches no turn,
// so approving it looks like it worked and changes nothing. Generated drafts can
// arrive that way (an unclassified turn whose question was all stopwords), and
// the row Approve button below bypasses the editor's `canSave` rule — so the row
// has to check too. The server rejects it as well; this is the readable half.
function triggerIsEmpty(f: AiFlow): boolean {
  return !((f.trigger?.intents?.length ?? 0) || (f.trigger?.keywords?.length ?? 0));
}

// Provenance block under a flow's title: what produced this draft, the customer
// question it was written about, and (for the nightly digest) why + the chats it
// was inferred from. Renders nothing for hand-authored flows.
function Provenance({ f }: { f: AiFlow }) {
  const evidence = Array.isArray(f.evidence) ? f.evidence : [];
  if (!f.source && !f.sourceQuestion && !f.rationale && evidence.length === 0 && !f.sourceRedacted) return null;
  const label = (f.source && SOURCE_LABEL[f.source]) || 'Generated';
  return (
    <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
      <span style={{ ...sx.badge('#7c3aed'), fontSize: 10 }}>{label}</span>
      {/* The server strips the quoted conversation for readers who are neither
          an admin nor the draft's author, so say why it is missing instead of
          rendering a provenance block that looks broken. */}
      {f.sourceRedacted && (
        <div style={{ marginTop: 3 }}>
          Source conversation hidden — visible to admins and the agent who wrote this rule.
        </div>
      )}
      {f.sourceQuestion && (
        <div style={{ marginTop: 3 }} dir="auto">
          Customer asked: “{clip(f.sourceQuestion, 120)}”
        </div>
      )}
      {f.rationale && (
        <div style={{ marginTop: 3 }} dir="auto">
          Why: {clip(f.rationale, 160)}
        </div>
      )}
      {evidence.length > 0 && (
        <div style={{ marginTop: 3 }} dir="auto">
          Seen in {evidence.length} chat{evidence.length === 1 ? '' : 's'}
          {evidence[0]?.reason ? `: “${clip(evidence[0].reason, 90)}”` : ''}
        </div>
      )}
      {(f.sourceChatId || evidence[0]?.chatId) && (
        <div style={{ marginTop: 3, color: '#9ca3af' }}>
          chat {(f.sourceChatId ?? evidence[0]?.chatId ?? '').slice(0, 12)}
        </div>
      )}
    </div>
  );
}

export function FlowsPanel({ canEdit }: { canEdit: boolean }) {
  const { claims, user } = useAuth();
  const tenantCode = claims?.tenantCode ?? '';
  const agentUid = user?.uid ?? '';
  const [items, setItems] = useState<AiFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantCode) return;
    setLoading(true);
    const r = await flowsApi.list(tenantCode);
    setItems(r.items);
    setLoading(false);
  }, [tenantCode]);

  useEffect(() => { void load(); }, [load]);

  const validSteps = useMemo(
    () => (draft?.steps ?? []).map((s) => s.trim()).filter(Boolean),
    [draft],
  );
  const canSave = !!draft && draft.title.trim().length > 0 && validSteps.length > 0
    && (draft.intents.length > 0 || draft.keywords.trim().length > 0);

  // Drafts are the review queue: rules taught from the inbox popover and the
  // nightly feedback digest both land here and do NOTHING until approved. The
  // count makes that queue visible instead of buried in the status column.
  const pendingCount = useMemo(() => items.filter((f) => f.status !== 'approved').length, [items]);

  async function save(approve: boolean) {
    if (!draft || !canSave) return;
    setSaving(true);
    setErr(null);
    try {
      const keywords = draft.keywords.split(',').map((k) => k.trim()).filter(Boolean);
      const saved = await flowsApi.save(tenantCode, agentUid, {
        ...(draft.id ? { id: draft.id } : {}),
        title: draft.title.trim(),
        trigger: { intents: draft.intents, keywords },
        steps: validSteps.map((text) => ({ text })),
        status: 'draft', // save always lands as draft; approve is a separate deliberate step
      } as Omit<AiFlow, 'updatedAt' | 'updatedBy'>);
      if (approve) await flowsApi.approve(tenantCode, saved.id, true);
      setDraft(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleApprove(f: AiFlow) {
    setErr(null);
    try {
      await flowsApi.approve(tenantCode, f.id, f.status !== 'approved');
    } catch (e) {
      // The server refuses to approve a flow with no trigger. Without this the
      // rejection was swallowed and the row just silently stayed a draft.
      setErr(e instanceof Error ? e.message : 'Approve failed');
    }
    await load();
  }
  async function remove(f: AiFlow) {
    if (!window.confirm(`Delete flow "${f.title}"?`)) return;
    await flowsApi.remove(tenantCode, f.id);
    await load();
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Support flows</h1>
      <p style={{ ...sx.hint, marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        Teach the bot a step-by-step playbook for a kind of request (e.g. "calls not working → ask location → check
        the balance → if a package is missing, escalate; otherwise tell the customer it's fine"). Only <b>approved</b>{' '}
        flows are used, so you can draft and review before anything goes live.
      </p>

      {pendingCount > 0 && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 16,
            fontSize: 13,
            color: '#92400e',
          }}
        >
          <b>{pendingCount}</b> draft{pendingCount === 1 ? '' : 's'} awaiting review. Drafts do not affect the bot until
          they are approved.
        </div>
      )}

      {/* Row actions (approve/delete) fail outside the editor card, where `err`
          used to be the only place an error was shown. */}
      {!draft && err && (
        <p style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{err}</p>
      )}

      {!draft && canEdit && (
        <button style={{ ...sx.btnPrimary, marginBottom: 16 }} onClick={() => setDraft({ ...EMPTY_DRAFT })}>
          + New flow
        </button>
      )}

      {draft && (
        <div style={sx.card}>
          <label style={sx.label}>Title</label>
          <input
            style={sx.input}
            value={draft.title}
            placeholder="e.g. Calls not working"
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>Fires on intent</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {/* The six above plus anything the stored draft already carries:
                  the nightly feedback job canonicalizes against ai-brain's FULL
                  category list (it also emits general/greeting/escalation/
                  off_domain), and a value with no pill would be invisible here
                  yet still saved back — an intent the reviewer cannot see or
                  remove. */}
              {[...INTENTS, ...draft.intents.filter((i) => !INTENTS.includes(i))].map((it) => {
                const on = draft.intents.includes(it);
                return (
                  <button
                    key={it}
                    type="button"
                    onClick={() => setDraft({
                      ...draft,
                      intents: on ? draft.intents.filter((x) => x !== it) : [...draft.intents, it],
                    })}
                    style={{
                      ...sx.btnSecondary,
                      padding: '4px 10px',
                      background: on ? '#eff6ff' : 'white',
                      borderColor: on ? '#93c5fd' : '#d1d5db',
                      color: on ? '#1d4ed8' : '#374151',
                      fontWeight: on ? 600 : 400,
                    }}
                  >
                    {it}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>…or keywords (comma-separated, optional)</label>
            <input
              style={sx.input}
              value={draft.keywords}
              placeholder="שיחות, אין קליטה, calls"
              onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>Steps (in order)</label>
            {draft.steps.map((s, i) => (
              <div key={i} style={{ ...sx.row, marginBottom: 6 }}>
                <span style={{ color: '#9ca3af', fontSize: 13, width: 18, textAlign: 'right' }}>{i + 1}.</span>
                <input
                  style={sx.input}
                  value={s}
                  placeholder={i === 0 ? 'e.g. Ask whether they are in Israel or abroad' : 'Next step…'}
                  onChange={(e) => {
                    const steps = draft.steps.slice();
                    steps[i] = e.target.value;
                    setDraft({ ...draft, steps });
                  }}
                />
                {draft.steps.length > 1 && (
                  <button
                    style={{ ...sx.btnDanger, padding: '4px 8px' }}
                    onClick={() => setDraft({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })}
                    aria-label="Remove step"
                  >×</button>
                )}
              </div>
            ))}
            <button
              style={{ ...sx.btnSecondary, marginTop: 4 }}
              onClick={() => setDraft({ ...draft, steps: [...draft.steps, ''] })}
            >+ Add step</button>
          </div>

          {err && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{err}</p>}

          <div style={{ ...sx.row, marginTop: 16, marginBottom: 0 }}>
            <button style={sx.btnPrimary} disabled={!canSave || saving} onClick={() => save(false)}>
              Save draft
            </button>
            <button
              style={{ ...sx.btnPrimary, background: '#059669' }}
              disabled={!canSave || saving}
              onClick={() => save(true)}
            >
              Save &amp; approve
            </button>
            <button style={sx.btnSecondary} disabled={saving} onClick={() => { setDraft(null); setErr(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={sx.hint}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={sx.hint}>No flows yet.</p>
      ) : (
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Title</th>
              <th style={sx.th}>Fires on</th>
              <th style={sx.th}>Steps</th>
              <th style={sx.th}>Status</th>
              <th style={sx.th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => {
              // Approving this would be a no-op: block it and say so in the row.
              const needsTrigger = triggerIsEmpty(f) && f.status !== 'approved';
              return (
              <tr key={f.id} style={f.status !== 'approved' ? { background: '#fffdf5' } : undefined}>
                <td style={{ ...sx.td, verticalAlign: 'top' }}>
                  <div dir="auto">{f.title}</div>
                  <Provenance f={f} />
                </td>
                <td style={{ ...sx.td, color: '#6b7280', fontSize: 12, verticalAlign: 'top' }}>
                  {[...(f.trigger?.intents ?? []), ...(f.trigger?.keywords ?? [])].join(', ') || (
                    <span style={{ color: '#b45309' }}>No trigger — edit and add one</span>
                  )}
                </td>
                <td style={{ ...sx.td, verticalAlign: 'top' }}>{f.steps.length}</td>
                <td style={{ ...sx.td, verticalAlign: 'top' }}>
                  <span style={sx.badge(f.status === 'approved' ? '#059669' : '#d97706')}>
                    {f.status === 'approved' ? 'Approved · live' : 'Draft · needs review'}
                  </span>
                </td>
                <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                  {canEdit && (
                    <>
                      <button style={{ ...sx.btnSecondary, marginRight: 6 }} onClick={() => setDraft(toDraft(f))}>Edit</button>
                      <button
                        style={{ ...sx.btnSecondary, marginRight: 6, ...(needsTrigger ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                        disabled={needsTrigger}
                        title={needsTrigger ? 'Add an intent or a keyword first — this flow would never fire' : undefined}
                        onClick={() => toggleApprove(f)}
                      >
                        {f.status === 'approved' ? 'Unapprove' : 'Approve'}
                      </button>
                      <button style={sx.btnDanger} onClick={() => remove(f)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
